import React, { useCallback, useRef, useEffect, useState } from 'react';
import { _partial } from 'underscore-es';
import { useHistory, useParams } from 'react-router-dom';

import { useToggle } from '../../../../libs/react-use-extra';
import { useEditor } from '../CampaignEditor';
import { FormSection } from '../../forms';
import {
  AddButton,
  Stage,
  StageList,
  Step,
  Container,
  StagesWrapper,
} from './Stages.styled';
import { useToast, useFullLoader } from '../../../../redux/actions/UI';
import { Order, Unit, createStage } from '../../api';
import { StageIcon } from '../../SVGs/Icons';
import generateObjectId from '../../utils/generateObjectId';

const DEFAULT_STAGES = [];
const REQUIRED_STAGES_COUNT = 1;

const Stages = () => {
  const history = useHistory();
  const { id: campaignIdFromParams } = useParams();

  const [isValidationVisible, { on: showValidation }] = useToggle();
  const stagesRef = useRef({});
  const [draggedStageId, setDraggedStageId] = useState(null);
  const draggedStageIdRef = useRef(null); // ref so drop handler always has current value
  const [dragOverState, setDragOverState] = useState(null); // { stageId, position: 'above' | 'below' }
  const lastDragOverRef = useRef(null); // avoid setState when unchanged (prevents freeze during drag)

  const editor = useEditor();
  const { errorToast } = useToast();
  const fullLoader = useFullLoader();

  const goToAutomationDashboard = useCallback(() => {
    history.push('/dashboard/automation');
  }, [editor]);

  const stages = editor.getField('stages') ?? DEFAULT_STAGES;

  const haveStages = stages.length > 0;

  const shouldAllowDeletingStages = stages.length > REQUIRED_STAGES_COUNT;

  const addStage = useCallback(async () => {
    // Get campaign ID from URL params or editor data
    const campaignIdFromEditor = editor.getField('_id');
    const campaignId = campaignIdFromParams || campaignIdFromEditor;

    // Prepare new stage data
    const newStageData = {
      name: `Stage ${stages.length + 1}`,
      conditions: [
        {
          name: null,
          delay: { value: 0, order: Order.AFTER, unit: Unit.DAY },
        },
      ],
    };

    // If campaign exists, create stage in backend first
    if (campaignId) {
      try {
        await fullLoader.during('Creating stage...', async () => {
          const createdStage = await createStage(campaignId, newStageData);
          // Use the stage returned from backend (includes backend _id)
          editor.setField('stages', [...stages, createdStage]);
        });
      } catch (error) {
        errorToast(
          `Failed to create stage: ${error.message || 'Unknown error'}`,
          5
        );
        // Fallback: create stage locally if backend creation fails
        const fallbackStage = {
          _id: generateObjectId(),
          ...newStageData,
          conditions: newStageData.conditions.map(condition => ({
            ...condition,
            _id: generateObjectId(),
          })),
        };
        editor.setField('stages', [...stages, fallbackStage]);
      }
    } else {
      // Campaign doesn't exist yet (new campaign) - create stage locally
      // It will be saved when the campaign is created
      const newStage = {
        _id: generateObjectId(),
        ...newStageData,
        conditions: newStageData.conditions.map(condition => ({
          ...condition,
          _id: generateObjectId(),
        })),
      };
      editor.setField('stages', [...stages, newStage]);
    }
  }, [editor, stages, campaignIdFromParams, fullLoader, errorToast]);

  useEffect(() => {
    if (!haveStages) {
      addStage().catch(error => {
        console.error('Failed to add initial stage:', error);
      });
    }
  }, [haveStages, addStage]);

  const handleCreate = useCallback(async () => {
    if (!isValidationVisible) {
      showValidation();
    }

    const stageRefs = Object.values(stagesRef.current);
    const areAllStagesValid = stageRefs.every(stage => stage.isValid());
    if (areAllStagesValid && haveStages) {
      editor.goToStep('audience/editor');
    } else if (!haveStages) {
      errorToast(
        "This campaign doesn't have any stages. Please add one and try again.",
        5
      );
    } else if (!areAllStagesValid) {
      errorToast(
        'Some stages are invalid. Please correct them and try again.',
        5
      );
    }
  }, [editor, haveStages, isValidationVisible, stages]);

  const updateStage = useCallback(
    (id, newStage) => {
      const newStages = stages.map(_stage => {
        return _stage._id === id ? newStage : _stage;
      });
      editor.setField('stages', newStages);
    },
    [editor, stages]
  );

  const createStageUpdater = useCallback(
    id => _partial(updateStage, id),
    [updateStage]
  );

  const createStageRef = useCallback(id => {
    return stage => {
      if (stage) {
        stagesRef.current[id] = stage;
      } else {
        delete stagesRef.current[id];
      }
    };
  }, []);

  const reorderStages = useCallback(
    (draggedId, targetId, position = 'above') => {
      // Get the current array of stages from the editor
      const currentStages = editor.getField('stages') ?? [];

      // Find the index of the stage being dragged by matching its ID
      const draggedIndex = currentStages.findIndex(
        stage => stage._id === draggedId
      );

      // Find the index of the target stage (where we want to drop the dragged stage)
      const targetIndex = currentStages.findIndex(
        stage => stage._id === targetId
      );

      // Early return if:
      // - Either stage wasn't found (findIndex returns -1)
      // - Or the dragged stage is already at the target position (no reordering needed)
      if (draggedIndex === -1 || targetIndex === -1) {
        return;
      }

      // Desired index for the dragged stage: above = targetIndex, below = targetIndex + 1
      let desiredIndex = position === 'below' ? targetIndex + 1 : targetIndex;
      desiredIndex = Math.max(0, Math.min(desiredIndex, currentStages.length));

      if (
        desiredIndex === draggedIndex ||
        (desiredIndex === draggedIndex + 1 && position === 'below')
      ) {
        return;
      }

      // Create a shallow copy of the stages array to avoid mutating the original
      const newStages = [...currentStages];

      // Remove the dragged stage from its current position
      // splice returns an array of removed elements, we destructure to get the first (and only) element
      const [removed] = newStages.splice(draggedIndex, 1);
      // After removal, if desiredIndex was after draggedIndex, indices shifted
      const insertIndex =
        desiredIndex > draggedIndex ? desiredIndex - 1 : desiredIndex;
      newStages.splice(insertIndex, 0, removed);

      editor.setField('stages', newStages);
    },
    [editor]
  );

  const handleDragStart = useCallback(stageId => {
    setDraggedStageId(stageId);
    draggedStageIdRef.current = stageId;
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedStageId(null);
    setDragOverState(null);
    draggedStageIdRef.current = null;
  }, []);

  const handleDragOver = useCallback(
    (e, targetStageId) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (!draggedStageId || draggedStageId === targetStageId) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      const position = e.clientY < mid ? 'above' : 'below';
      // Only update state when target or position changed – avoids hundreds of re-renders per second and frozen UI
      const next = { stageId: targetStageId, position };
      if (
        lastDragOverRef.current?.stageId !== next.stageId ||
        lastDragOverRef.current?.position !== next.position
      ) {
        lastDragOverRef.current = next;
        setDragOverState(next);
      }
    },
    [draggedStageId]
  );

  const handleDragLeave = useCallback(e => {
    const related = e.relatedTarget;
    if (related != null && e.currentTarget.contains(related)) return;
    if (lastDragOverRef.current != null) {
      lastDragOverRef.current = null;
      setDragOverState(null);
    }
  }, []);

  const handleDrop = useCallback(
    (e, targetStageId) => {
      e.preventDefault();
      e.stopPropagation();
      lastDragOverRef.current = null;
      const droppedDraggedId = draggedStageIdRef.current ?? draggedStageId;
      if (droppedDraggedId && droppedDraggedId !== targetStageId) {
        const position =
          dragOverState?.stageId === targetStageId
            ? dragOverState.position
            : 'above';
        reorderStages(droppedDraggedId, targetStageId, position);
      }
      setDraggedStageId(null);
      setDragOverState(null);
      draggedStageIdRef.current = null;
    },
    [draggedStageId, dragOverState]
  );

  return (
    <Step
      title="Stages"
      description="Stages determine when and what gets sent to contacts."
      nextText="Continue"
      onBack={goToAutomationDashboard}
      onNext={handleCreate}
    >
      <Container>
        <FormSection
          IconComponent={StageIcon}
          title="Stages Builder"
          description={
            'Enter stages of your campaign. Stages determine when and what gets sent to contacts. Drag stages to reorder them.'
          }
          infoContainer={true}
        >
          <StagesWrapper>
            {haveStages && (
              <StageList>
                {stages.map(stage => {
                  const dragOver =
                    dragOverState?.stageId === stage._id
                      ? dragOverState.position
                      : null;
                  return (
                    <StageList.Item
                      key={stage._id}
                      onDragOver={e => handleDragOver(e, stage._id)}
                      onDragLeave={handleDragLeave}
                      onDrop={e => handleDrop(e, stage._id)}
                      isDragging={draggedStageId === stage._id}
                      dragOverPosition={dragOver}
                    >
                      <Stage
                        ref={createStageRef(stage._id)}
                        value={stage}
                        onChange={createStageUpdater(stage._id)}
                        isValidationVisible={isValidationVisible}
                        shouldAllowDeletingStages={shouldAllowDeletingStages}
                        onDragStart={() => handleDragStart(stage._id)}
                        onDragEnd={handleDragEnd}
                        onDragOver={e => handleDragOver(e, stage._id)}
                        onDrop={e => handleDrop(e, stage._id)}
                        isDragging={draggedStageId === stage._id}
                      />
                    </StageList.Item>
                  );
                })}
              </StageList>
            )}
            <AddButton onClick={addStage} isHighlighted={!haveStages}>
              <AddButton.Icon />
              <AddButton.Label>
                Add Stage{' '}
                <AddButton.Label.HelperText>
                  (optional)
                </AddButton.Label.HelperText>
              </AddButton.Label>
              {!haveStages && (
                <AddButton.ValidityIndicator
                  isVisible={isValidationVisible}
                  isValid={haveStages}
                />
              )}
            </AddButton>
          </StagesWrapper>
        </FormSection>
      </Container>
    </Step>
  );
};

export default Stages;
